import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Link
} from '@mui/material';
import {
  Add,
  Edit,
  CheckCircle,
  Cancel,
  Schedule,
  AttachFile
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { useToast } from '../../contexts/ToastContext';
import StoreDocumentsDialog from './StoreDocumentsDialog';
import ToolDetailModal from './ToolDetailModal';

const toDateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EMPTY_SUMMARY = {
  total: 0,
  calibrated: 0,
  dueSoon: 0,
  overdue: 0,
  unavailable: 0
};

const canIssueTool = (tool) => (
  !tool.assignedTo
  && tool.serviceabilityStatus === 'SERVICEABLE'
  && tool.calibrationStatus !== 'OVERDUE'
  && tool.calibrationStatus !== 'DUE'
);

const ToolControl = () => {
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [open, setOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [calibrateOpen, setCalibrateOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [detailToolId, setDetailToolId] = useState(null);
  const [formData, setFormData] = useState({
    toolNumber: '',
    description: '',
    category: 'HAND_TOOL',
    calibrationDueDate: ''
  });
  const [issueData, setIssueData] = useState({
    assignedTo: ''
  });
  const [calibrationData, setCalibrationData] = useState({
    calibratedDate: '',
    certificateNumber: '',
    nextDueDate: '',
    notes: ''
  });
  const [documentsDialog, setDocumentsDialog] = useState({
    open: false,
    entityId: null,
    label: ''
  });
  const { user } = useAuth();
  const { showToast } = useToast();

  const canEdit = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get('/api/store/tools');
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
      showToast('Error loading tools', 'error');
    }
  }, [showToast]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get('/api/store/tools/calibration-summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching calibration summary:', error);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchTools(), fetchSummary()]);
  }, [fetchTools, fetchSummary]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  useEffect(() => {
    refreshAll();
    fetchUsers();
  }, [refreshAll, fetchUsers]);

  const handleOpen = (tool = null) => {
    if (tool) {
      setSelectedTool(tool);
      setFormData({
        toolNumber: tool.toolNumber,
        description: tool.description,
        category: tool.category,
        calibrationDueDate: toDateInputValue(tool.calibrationDueDate)
      });
    } else {
      setSelectedTool(null);
      setFormData({
        toolNumber: '',
        description: '',
        category: 'HAND_TOOL',
        calibrationDueDate: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTool(null);
  };

  const handleIssueOpen = (tool) => {
    setSelectedTool(tool);
    setIssueData({ assignedTo: '' });
    setIssueOpen(true);
  };

  const handleIssueClose = () => {
    setIssueOpen(false);
    setSelectedTool(null);
  };

  const handleCalibrateOpen = (tool) => {
    setSelectedTool(tool);
    setCalibrationData({
      calibratedDate: new Date().toISOString().split('T')[0],
      certificateNumber: '',
      nextDueDate: '',
      notes: ''
    });
    setCalibrateOpen(true);
  };

  const handleCalibrateClose = () => {
    setCalibrateOpen(false);
    setSelectedTool(null);
  };

  const handleDocumentsDialogOpen = (tool) => {
    setDocumentsDialog({
      open: true,
      entityId: tool._id,
      label: `${tool.toolNumber} - ${tool.description}`
    });
  };

  const handleDocumentsDialogClose = () => {
    setDocumentsDialog({ open: false, entityId: null, label: '' });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        toolNumber: formData.toolNumber,
        description: formData.description,
        category: formData.category,
        calibrationDueDate: formData.calibrationDueDate ? formData.calibrationDueDate : null
      };
      if (selectedTool) {
        await axios.put(`/api/store/tools/${selectedTool._id}`, payload);
        showToast('Tool updated', 'success');
      } else {
        await axios.post('/api/store/tools', payload);
        showToast('Tool created', 'success');
      }
      refreshAll();
      handleClose();
    } catch (error) {
      console.error('Error saving tool:', error);
      showToast(error.response?.data?.message || 'Error saving tool', 'error');
    }
  };

  const handleIssue = async () => {
    try {
      await axios.post(`/api/store/tools/${selectedTool._id}/issue`, issueData);
      showToast('Tool issued successfully', 'success');
      refreshAll();
      handleIssueClose();
    } catch (error) {
      console.error('Error issuing tool:', error);
      showToast(error.response?.data?.message || 'Error issuing tool', 'error');
    }
  };

  const handleReturn = async (toolId) => {
    try {
      await axios.post(`/api/store/tools/${toolId}/return`);
      showToast('Tool returned successfully', 'success');
      refreshAll();
    } catch (error) {
      console.error('Error returning tool:', error);
      showToast(error.response?.data?.message || 'Error returning tool', 'error');
    }
  };

  const handleCalibrate = async () => {
    try {
      await axios.put(`/api/store/tools/${selectedTool._id}/calibrate`, calibrationData);
      showToast('Tool calibration updated', 'success');
      refreshAll();
      handleCalibrateClose();
    } catch (error) {
      console.error('Error updating calibration:', error);
      showToast(error.response?.data?.message || 'Error updating calibration', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Tool Control</Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
            Add Tool
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total tools</Typography>
              <Typography variant="h5">{summary.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Calibrated</Typography>
              <Typography variant="h5" color="success.main">{summary.calibrated}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Due within 30 days</Typography>
              <Typography variant="h5" color="warning.main">{summary.dueSoon}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
              <Typography variant="h5" color="error.main">{summary.overdue}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tool Number</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Calibration Status</TableCell>
              <TableCell>Serviceability</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Calibration Due</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tools found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tools.map((tool) => (
                <TableRow key={tool._id}>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setDetailToolId(tool._id)}
                      sx={{ fontFamily: 'monospace', fontWeight: 'bold', textAlign: 'left' }}
                    >
                      {tool.toolNumber}
                    </Link>
                  </TableCell>
                  <TableCell>{tool.description}</TableCell>
                  <TableCell>
                    <Chip label={tool.category} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tool.calibrationStatus}
                      size="small"
                      color={
                        tool.calibrationStatus === 'CALIBRATED'
                          ? 'success'
                          : tool.calibrationStatus === 'OVERDUE'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tool.serviceabilityStatus}
                      size="small"
                      color={
                        tool.serviceabilityStatus === 'SERVICEABLE'
                          ? 'success'
                          : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {tool.assignedTo ? (
                      <Typography variant="body2">
                        {tool.assignedTo.firstName} {tool.assignedTo.lastName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Available
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {tool.calibrationDueDate && (
                      <Typography
                        variant="body2"
                        color={
                          new Date(tool.calibrationDueDate) < new Date()
                            ? 'error'
                            : 'text.secondary'
                        }
                      >
                        {new Date(tool.calibrationDueDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Attachments">
                        <IconButton size="small" onClick={() => handleDocumentsDialogOpen(tool)}>
                          <AttachFile fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Edit Tool">
                          <IconButton size="small" onClick={() => handleOpen(tool)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canIssueTool(tool) && canEdit && (
                        <Tooltip title="Issue Tool">
                          <IconButton size="small" onClick={() => handleIssueOpen(tool)}>
                            <CheckCircle fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {tool.assignedTo && canEdit && (
                        <Tooltip title="Return Tool">
                          <IconButton size="small" onClick={() => handleReturn(tool._id)}>
                            <Cancel fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip title="Update Calibration">
                          <IconButton size="small" onClick={() => handleCalibrateOpen(tool)}>
                            <Schedule fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTool ? 'Edit Tool' : 'Add Tool'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tool Number"
                value={formData.toolNumber}
                onChange={(e) => setFormData({ ...formData, toolNumber: e.target.value })}
                required
                disabled={!!selectedTool}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value="HAND_TOOL">Hand Tool</MenuItem>
                  <MenuItem value="POWER_TOOL">Power Tool</MenuItem>
                  <MenuItem value="MEASUREMENT">Measurement</MenuItem>
                  <MenuItem value="GROUND_SUPPORT">Ground Support</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Calibration Due"
                type="date"
                value={formData.calibrationDueDate}
                onChange={(e) => setFormData({ ...formData, calibrationDueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Optional — next calibration due date"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedTool ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issueOpen} onClose={handleIssueClose} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Tool</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Issue To</InputLabel>
                <Select
                  value={issueData.assignedTo}
                  label="Issue To"
                  onChange={(e) => setIssueData({ ...issueData, assignedTo: e.target.value })}
                >
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIssueClose}>Cancel</Button>
          <Button onClick={handleIssue} variant="contained" disabled={!issueData.assignedTo}>
            Issue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={calibrateOpen} onClose={handleCalibrateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Update Calibration</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Calibrated Date"
                type="date"
                value={calibrationData.calibratedDate}
                onChange={(e) => setCalibrationData({ ...calibrationData, calibratedDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Next Due Date"
                type="date"
                value={calibrationData.nextDueDate}
                onChange={(e) => setCalibrationData({ ...calibrationData, nextDueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Certificate Number"
                value={calibrationData.certificateNumber}
                onChange={(e) => setCalibrationData({ ...calibrationData, certificateNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={calibrationData.notes}
                onChange={(e) => setCalibrationData({ ...calibrationData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCalibrateClose}>Cancel</Button>
          <Button onClick={handleCalibrate} variant="contained">
            Update Calibration
          </Button>
        </DialogActions>
      </Dialog>

      <StoreDocumentsDialog
        open={documentsDialog.open}
        onClose={handleDocumentsDialogClose}
        entityId={documentsDialog.entityId}
        entityType="tool"
        entityLabel={documentsDialog.label}
      />

      <ToolDetailModal
        toolId={detailToolId}
        open={Boolean(detailToolId)}
        onClose={() => setDetailToolId(null)}
        onToolUpdated={refreshAll}
      />
    </Box>
  );
};

export default ToolControl;
